/* eslint-disable no-param-reassign */
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import TerrainIcon from "@mui/icons-material/Terrain";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Accordion,
  AccordionSummary,
  Avatar,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import * as Cesium from "cesium";
import getEPSGCode from "get-epsg-code";
import React from "react";
import { Choose } from "react-extras";
import { useTranslation } from "react-i18next";
import { useMap3DObjectApi } from "~/api/GeneratedApi";
import { setTerrainAdjustmentDialogOpen } from "~/redux/reducers/mapReducer";
import { useAppDispatch } from "~/redux/store/store";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { SavedElevationSettings } from "../functions/colorAdjustments";
import {
  CesiumTerrainItem,
  CustomTerrainProvider,
} from "../types/CesiumMapItems";
import { terrainCanvasId } from "./adjustment/TerrainColorAdjustment";
import { DrawerItem } from "./CesiumDrawerDataItems";

interface Props {
  item: DrawerItem<CesiumTerrainItem>;
  viewer: Cesium.Viewer;
  onTerrainChange: () => void;
  terrainChanged: boolean;
}

const loadTerrain = (
  url: string,
  token: string,
): Cesium.TerrainProvider | undefined => {
  try {
    if (!token) return undefined;
    const resource = new Cesium.Resource({
      url,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      retryAttempts: 0,
    });
    const provider = new Cesium.CesiumTerrainProvider({
      url: resource,
    });
    return provider;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

export const CesiumDrawerTerrainItem = ({
  item,
  viewer,
  onTerrainChange,
  terrainChanged,
}: Props): JSX.Element => {
  const { t } = useTranslation("cesium");
  const { getMapAreaMapObject } = useMap3DObjectApi();
  const [loadId, setLoadId] = React.useState<number>();
  const [loaded, setLoaded] = React.useState(false);
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const isSame =
      (viewer.terrainProvider as CustomTerrainProvider).customId ===
      item.mapItem.id;
    setLoaded(isSame);
    if (!isSame) setLoadId(undefined);
  }, [item.mapItem.id, terrainChanged, viewer.terrainProvider]);

  const { isLoading: backendLoading } = useCustomQuery(
    { key: "map-area-map-object", id: loadId },
    () => getMapAreaMapObject(loadId!),
    {
      enabled: !!loadId,
      onSuccess: (d) => {
        if (!d.url || !d.accessToken) return;
        const ter = loadTerrain(d.url, d.accessToken);
        if (!ter) return;
        viewer.terrainProvider = ter;
        (viewer.terrainProvider as CustomTerrainProvider).customId =
          item.mapItem.id;
        (viewer.terrainProvider as CustomTerrainProvider).minHeight = d.minZ;
        (viewer.terrainProvider as CustomTerrainProvider).maxHeight = d.maxZ;
        const proj = d.wkt ? getEPSGCode(d.wkt) : undefined;
        (viewer.terrainProvider as CustomTerrainProvider).projection = proj
          ? `EPSG:${proj}`
          : undefined;
        const parsed = d.elevationSettings
          ? (JSON.parse(d.elevationSettings) as SavedElevationSettings)
          : undefined;
        parsed?.colorsAndSteps?.forEach((s) => {
          if (s.isLowest) s.stepMin = Number.NEGATIVE_INFINITY;
          if (s.isHighest) s.stepMax = Number.POSITIVE_INFINITY;
        });
        (viewer.terrainProvider as CustomTerrainProvider).elevationSettings =
          parsed;
        onTerrainChange();
      },
    },
  );

  const handleDownloadClick = () => {
    if (loadId !== undefined) return;
    setLoadId(item.mapItem.id);
  };

  const handleRemoveClick = () => {
    if (!viewer.terrainProvider) return;
    viewer.scene.globe.material = undefined as unknown as Cesium.Material; // Assign as undefined when resetting material
    document.getElementById(terrainCanvasId)?.remove();
    viewer.terrainProvider = new Cesium.EllipsoidTerrainProvider({});
    onTerrainChange();
    setLoadId(undefined);
    setLoaded(false);
  };

  const handleAdjustClick = React.useCallback(() => {
    if (!viewer.terrainProvider) return;
    dispatch(setTerrainAdjustmentDialogOpen(true));
  }, [dispatch, viewer.terrainProvider]);

  const loading = React.useMemo(() => backendLoading, [backendLoading]);

  return (
    <Accordion
      expanded={false}
      elevation={1}
      TransitionProps={{ unmountOnExit: true }}
      disableGutters
      square
      sx={{
        backgroundColor: (theme) => theme.palette.primary.semiLight,
        color: (theme) => theme.palette.primary.contrastText,
        width: "100%",
      }}
    >
      <AccordionSummary sx={{ cursor: "default", alignItems: "center" }}>
        <Grid container justifyContent="center">
          <Grid
            item
            container
            xs
            flexDirection="row"
            alignItems="center"
            flexWrap="nowrap"
            sx={{ flexBasis: "100%" }}
          >
            <Grid item sx={{ mr: 1 }}>
              <Tooltip title={t("terrain").toString()}>
                <Avatar
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: (theme) => theme.palette.primary.contrastText,
                  }}
                >
                  <TerrainIcon />
                </Avatar>
              </Tooltip>
            </Grid>
            <Grid item>
              <Typography sx={{ m: 1, wordBreak: "break-all" }}>
                {item.mapItem.name}
              </Typography>
            </Grid>
          </Grid>
          <Choose>
            <Choose.When
              condition={!loading && !loaded}
              render={() => (
                <Tooltip title={t("download-data").toString()}>
                  <Grid>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadClick();
                      }}
                      size="large"
                    >
                      <GetAppIcon
                        sx={{
                          color: (theme) => theme.palette.primary.contrastText,
                        }}
                      />
                    </IconButton>
                  </Grid>
                </Tooltip>
              )}
            />
            <Choose.When
              condition={loading}
              render={() => (
                <Grid>
                  <CircularProgress
                    size={24}
                    sx={{
                      margin: (theme) => `auto ${theme.spacing(1)}`,
                      color: (theme) => theme.palette.primary.contrastText,
                    }}
                  />
                </Grid>
              )}
            />
            <Choose.When
              condition={!loading && loaded}
              render={() => (
                <>
                  <Tooltip title={t("remove-from-map").toString()}>
                    <Grid>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveClick();
                        }}
                        size="large"
                      >
                        <CloseIcon
                          sx={{
                            color: (theme) =>
                              theme.palette.primary.contrastText,
                          }}
                        />
                      </IconButton>
                    </Grid>
                  </Tooltip>
                  <Tooltip title={t("adjust-terrain").toString()}>
                    <Grid>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAdjustClick();
                        }}
                        size="large"
                      >
                        <TuneIcon
                          sx={{
                            color: (theme) =>
                              theme.palette.primary.contrastText,
                          }}
                        />
                      </IconButton>
                    </Grid>
                  </Tooltip>
                </>
              )}
            />
          </Choose>
        </Grid>
      </AccordionSummary>
    </Accordion>
  );
};
